/**
 * API types and utilities for the sandbox backend
 * 
 * Defines the REST API contract between the sandbox frontend and backend.
 */

import type { 
  SaveSandboxRequest, 
  SaveSandboxResponse, 
  LoadSandboxResponse,
  SandboxListOptions,
  SandboxListResponse 
} from './persistence';

/**
 * API endpoint paths
 */
export const API_ENDPOINTS = {
  /** Save a new sandbox or create a new revision */
  SAVE: '/api/sandbox',
  /** Load a sandbox by hash (optionally with revision) */
  LOAD: '/api/sandbox/:hash/:revision?',
  /** List sandboxes with filtering/pagination */
  LIST: '/api/sandboxes',
  /** Get revisions for a sandbox */
  REVISIONS: '/api/sandbox/:hash/revisions',
  /** Fork a sandbox */
  FORK: '/api/sandbox/:hash/fork',
  /** Health check */
  HEALTH: '/api/health',
} as const;

/**
 * API error response
 */
export interface ApiError {
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

/** Pluggable per-sandbox edit-token store (localStorage in the app; in-memory in tests). */
export interface EditTokenStore {
  get(id: string): string | undefined;
  set(id: string, token: string): void;
}

/** Thrown for any non-2xx API response; carries the stable code + parsed body. */
export class ApiRequestError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly body?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
  }
}
/** 409: the head advanced since the client loaded it (Tier-1 optimistic concurrency). */
export class RevisionConflictError extends ApiRequestError {
  constructor(readonly headRevision: number, message: string, body?: unknown) {
    super(409, 'REVISION_CONFLICT', message, body);
    this.name = 'RevisionConflictError';
  }
}
/** 403: append rejected — no/invalid token, or a locked (backfilled) lineage. Loss = fork. */
export class EditTokenError extends ApiRequestError {
  constructor(code: string, message: string) {
    super(403, code, message);
    this.name = 'EditTokenError';
  }
}

export interface ApiClientOptions {
  editTokenStore?: EditTokenStore;
}

/**
 * API error codes
 */
export const API_ERROR_CODES = {
  NOT_FOUND: 'SANDBOX_NOT_FOUND',
  INVALID_HASH: 'INVALID_HASH',
  INVALID_REVISION: 'INVALID_REVISION',
  INVALID_DATA: 'INVALID_DATA',
  COMPRESSION_FAILED: 'COMPRESSION_FAILED',
  STORAGE_ERROR: 'STORAGE_ERROR',
  RATE_LIMITED: 'RATE_LIMITED',
  SERVER_ERROR: 'SERVER_ERROR',
} as const;

/**
 * Rate limit headers
 */
export const RATE_LIMIT_HEADERS = {
  LIMIT: 'X-RateLimit-Limit',
  REMAINING: 'X-RateLimit-Remaining',
  RESET: 'X-RateLimit-Reset',
} as const;

/**
 * Content-Type for compressed sandbox data
 */
export const COMPRESSED_CONTENT_TYPE = 'application/x-gotti-sandbox+lz';

/**
 * Build a URL for loading a sandbox
 */
export function buildLoadUrl(baseUrl: string, hash: string, revision?: number): string {
  let url = `${baseUrl}/api/sandbox/${hash}`;
  if (revision !== undefined) {
    url += `/${revision}`;
  }
  return url;
}

/**
 * Build a URL for the sandbox editor with a specific sandbox loaded
 */
export function buildEditorUrl(baseUrl: string, hash: string, revision?: number): string {
  if (revision !== undefined) {
    return `${baseUrl}/${hash}/${revision}`;
  }
  return `${baseUrl}/${hash}`;
}

/**
 * Client-side API wrapper interface
 */
export interface SandboxApiClient {
  /**
   * Save a sandbox (create new or new revision)
   */
  save(request: SaveSandboxRequest): Promise<SaveSandboxResponse>;
  
  /**
   * Load a sandbox by hash and optional revision
   */
  load(hash: string, revision?: number): Promise<LoadSandboxResponse>;
  
  /**
   * List sandboxes with filtering/pagination
   */
  list(options?: SandboxListOptions): Promise<SandboxListResponse>;
  
  /**
   * Fork a sandbox
   */
  fork(hash: string, revision?: number): Promise<SaveSandboxResponse>;
  
  /**
   * Get all revisions for a sandbox
   */
  getRevisions(hash: string): Promise<{ revision: number; createdAt: string; name: string }[]>;
}

/** Shape the backend uses for a non-2xx JSON body. */
interface ApiErrorBody {
  error?: { code?: string; message?: string };
  headRevision?: number;
}

/**
 * Create an API client for the sandbox backend
 */
export function createApiClient(baseUrl: string, opts: ApiClientOptions = {}): SandboxApiClient {
  const store = opts.editTokenStore;

  const request = async <T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string,
    body?: unknown,
    headers?: Record<string, string>,
  ): Promise<T> => {
    const response = await fetch(`${baseUrl}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json', ...(headers ?? {}) },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      let parsed: ApiErrorBody | undefined;
      // `Response.json()` is untyped by construction (it is whatever the server
      // sent); the reads below are all optional-chained, so a body that does not
      // match still degrades to the SERVER_ERROR defaults rather than throwing.
      try { parsed = (await response.json()) as ApiErrorBody; } catch { /* non-JSON error body */ }
      const code = parsed?.error?.code ?? 'SERVER_ERROR';
      const message = parsed?.error?.message ?? `Request failed (${response.status})`;
      if (response.status === 409 && code === 'REVISION_CONFLICT') {
        throw new RevisionConflictError(parsed?.headRevision ?? 0, message, parsed);
      }
      if (response.status === 403 && (code === 'EDIT_TOKEN_REQUIRED' || code === 'EDIT_TOKEN_INVALID')) {
        throw new EditTokenError(code, message);
      }
      throw new ApiRequestError(response.status, code, message, parsed);
    }

    return response.json() as Promise<T>;
  };

  const captureToken = (resp: SaveSandboxResponse): SaveSandboxResponse => {
    if (resp.editToken && store) store.set(resp.hash, resp.editToken);
    return resp;
  };

  return {
    async save(req: SaveSandboxRequest): Promise<SaveSandboxResponse> {
      const headers: Record<string, string> = {};
      // Append (has parent, not a fork): attach the lineage's token if we hold one.
      if (req.parentHash && !req.fork) {
        const token = store?.get(req.parentHash);
        if (token) headers['X-Sandbox-Edit-Token'] = token;
      }
      return captureToken(await request<SaveSandboxResponse>('POST', '/api/sandbox', req, headers));
    },

    async load(hash: string, revision?: number): Promise<LoadSandboxResponse> {
      const path = revision !== undefined ? `/api/sandbox/${hash}/${revision}` : `/api/sandbox/${hash}`;
      return request('GET', path);
    },

    async list(options?: SandboxListOptions): Promise<SandboxListResponse> {
      const params = new URLSearchParams();
      if (options?.query) params.set('q', options.query);
      if (options?.tags) params.set('tags', options.tags.join(','));
      if (options?.author) params.set('author', options.author);
      if (options?.sortBy) params.set('sort', options.sortBy);
      if (options?.sortOrder) params.set('order', options.sortOrder);
      if (options?.offset !== undefined) params.set('offset', String(options.offset));
      if (options?.limit !== undefined) params.set('limit', String(options.limit));
      const qs = params.toString();
      return request('GET', qs ? `/api/sandboxes?${qs}` : '/api/sandboxes');
    },

    async fork(hash: string, revision?: number): Promise<SaveSandboxResponse> {
      const path = revision !== undefined ? `/api/sandbox/${hash}/${revision}/fork` : `/api/sandbox/${hash}/fork`;
      return captureToken(await request<SaveSandboxResponse>('POST', path));
    },

    async getRevisions(hash: string): Promise<{ revision: number; createdAt: string; name: string }[]> {
      return request('GET', `/api/sandbox/${hash}/revisions`);
    },
  };
}
